import React, { createContext, useCallback, useContext, useMemo, useState, useEffect } from 'react';
import { api } from '../services/api';

const AuthContext = createContext(null);

const safeJsonParse = (value) => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => safeJsonParse(localStorage.getItem('user')));
  const [token, setToken] = useState(() => localStorage.getItem('token'));

  const saveSession = useCallback((authData) => {
    if (authData?.token) {
      localStorage.setItem('token', authData.token);
      setToken(authData.token);
    }
    if (authData?.user) {
      localStorage.setItem('user', JSON.stringify(authData.user));
      setUser(authData.user);
    }
  }, []);

  const clearSession = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  }, []);

  const login = useCallback((credentials) => api.loginUser(credentials), []);
  const signup = useCallback((payload) => api.signupUser(payload), []);

  const verifyLoginOtp = useCallback(
    async (payload) => {
      const data = await api.verifyLoginOtp(payload);
      saveSession(data);
      return data;
    },
    [saveSession]
  );

  const verifySignupOtp = useCallback(
    async (payload) => {
      const data = await api.verifySignupOtp(payload);
      saveSession(data);
      return data;
    },
    [saveSession]
  );

  const resendOtp = useCallback((payload) => api.resendOtp(payload), []);
  const forgotPassword = useCallback((payload) => api.forgetPassword(payload), []);
  const verifyForgotPasswordOtp = useCallback(
    (payload) => api.verifyForgetPasswordOtp(payload),
    []
  );
  const updatePassword = useCallback((payload) => api.updatePassword(payload), []);
  const getProfile = useCallback(async () => {
    const data = await api.getProfile();
    if (data?.user) {
      localStorage.setItem('user', JSON.stringify(data.user));
      setUser(data.user);
    }
    return data;
  }, []);

  const logout = useCallback(() => {
    clearSession();
  }, [clearSession]);

  useEffect(() => {
    if (!token) return;
    getProfile().catch(() => {});
  }, [token, getProfile]);

  const value = useMemo(
    () => ({
      user,
      token,
      isAuthenticated: Boolean(token),
      login,
      signup,
      verifyLoginOtp,
      verifySignupOtp,
      resendOtp,
      forgotPassword,
      verifyForgotPasswordOtp,
      updatePassword,
      getProfile,
      logout
    }),
    [
      user,
      token,
      login,
      signup,
      verifyLoginOtp,
      verifySignupOtp,
      resendOtp,
      forgotPassword,
      verifyForgotPasswordOtp,
      updatePassword,
      getProfile,
      logout
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export { AuthProvider, useAuth };
